/**
 * Pure crowd aggregation. No recommendation weights.
 *
 * Denominators are submitted active participants.
 * Each participant counts at most once per Style or Flavor.
 * Tie-break is the fixed definition order, never selection order.
 */

import {
  FEELS,
  FEEL_IDS,
  FLAVORS,
  FLAVOR_IDS,
  STYLES,
  STYLE_IDS,
} from './definitions';
import { canonicalizeVibeSelection, isCompleteVibeSelection } from './selection';
import { isParticipantOnline } from '../utils/presence';
import { mixFeelColors, NEUTRAL_ORB_COLOR } from './orbVisual';

export const NEUTRAL_FEEL_ORB = NEUTRAL_ORB_COLOR;

export function isEligibleParticipant(row) {
  if (!row || typeof row.id !== 'string' || row.id.length === 0) return false;
  if (row.role !== 'host' && row.role !== 'guest') return false;
  return true;
}

/** Active party members, using the existing online-presence model. */
export function eligibleParticipants(participants, presenceByUid) {
  if (!Array.isArray(participants)) return [];
  return participants.filter((row) => (
    isEligibleParticipant(row) && isParticipantOnline(row.id, presenceByUid)
  ));
}

export function normalizeStoredPreference(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const selection = canonicalizeVibeSelection(raw);
  if (!isCompleteVibeSelection(selection)) return null;
  return {
    schemaVersion: 1,
    feelId: selection.feelId,
    styleIds: selection.styleIds,
    flavorIds: selection.flavorIds,
    submittedAt: typeof raw.submittedAt === 'number' ? raw.submittedAt : null,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : null,
  };
}

export function collectSubmittedVibes(participants, presenceByUid, preferencesByUid) {
  const eligible = eligibleParticipants(participants, presenceByUid);
  const submitted = [];
  eligible.forEach((person) => {
    const preference = normalizeStoredPreference(preferencesByUid?.[person.id]);
    if (!preference) return;
    submitted.push({
      feelId: preference.feelId,
      styleIds: preference.styleIds,
      flavorIds: preference.flavorIds,
    });
  });
  return {
    eligibleCount: eligible.length,
    submitted,
  };
}

function positiveCounts(items, pickIds, vocabularyIds) {
  const counts = {};
  items.forEach((item) => {
    const unique = new Set(
      pickIds(item).filter((id) => vocabularyIds.includes(id)),
    );
    unique.forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
  });
  return counts;
}

export function percentOf(count, total) {
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (!Number.isFinite(count) || count <= 0) return 0;
  return Math.round((count / total) * 100);
}

export function topSupported(definitions, counts, limit) {
  return definitions
    .map((item, index) => ({
      id: item.id,
      label: item.label,
      count: counts[item.id] || 0,
      order: index,
    }))
    .filter((item) => item.count > 0)
    .sort((left, right) => (right.count - left.count) || (left.order - right.order))
    .slice(0, limit)
    .map(({ id, label, count }) => ({ id, label, count }));
}

/**
 * Display-only orb mix. Counts stay weighted; the visual is a blended core
 * plus per-Feel anchors instead of a segmented donut.
 */
export function buildFeelRing(feelCounts, submittedCount) {
  const total = Number(submittedCount);
  if (!Number.isFinite(total) || total <= 0) {
    return {
      empty: true,
      mixColor: NEUTRAL_FEEL_ORB,
      glowColors: [NEUTRAL_FEEL_ORB],
      noteColor: NEUTRAL_FEEL_ORB,
      anchors: [],
      segments: [],
    };
  }

  const anchors = FEELS
    .map((feel) => {
      const count = feelCounts?.[feel.id] || 0;
      return {
        feelId: feel.id,
        color: feel.color,
        count,
        weight: count / total,
      };
    })
    .filter((item) => item.count > 0);

  if (anchors.length === 0) {
    return {
      empty: true,
      mixColor: NEUTRAL_FEEL_ORB,
      glowColors: [NEUTRAL_FEEL_ORB],
      noteColor: NEUTRAL_FEEL_ORB,
      anchors: [],
      segments: [],
    };
  }

  const mixColor = mixFeelColors(anchors) || NEUTRAL_FEEL_ORB;
  const dominant = anchors.reduce((current, item) => (
    item.weight > current.weight ? item : current
  ), anchors[0]);

  return {
    empty: false,
    mixColor,
    glowColors: anchors.map((item) => item.color),
    noteColor: dominant.color,
    anchors,
    segments: anchors,
  };
}

export function aggregateCrowd({ participants, presenceByUid, preferencesByUid }) {
  const { eligibleCount, submitted } = collectSubmittedVibes(
    participants,
    presenceByUid,
    preferencesByUid,
  );
  const submittedCount = submitted.length;
  const feelCounts = positiveCounts(submitted, (item) => [item.feelId], FEEL_IDS);
  const styleCounts = positiveCounts(submitted, (item) => item.styleIds, STYLE_IDS);
  const flavorCounts = positiveCounts(submitted, (item) => item.flavorIds, FLAVOR_IDS);

  const feelStats = FEELS
    .filter((feel) => feelCounts[feel.id])
    .map((feel) => ({
      id: feel.id,
      label: feel.label,
      color: feel.color,
      count: feelCounts[feel.id],
      percent: percentOf(feelCounts[feel.id], submittedCount),
    }));

  return {
    eligibleCount,
    submittedCount,
    feelCounts,
    styleCounts,
    flavorCounts,
    feelStats,
    topStyles: topSupported(STYLES, styleCounts, 4).map((item) => ({
      ...item,
      percent: percentOf(item.count, submittedCount),
    })),
    topFlavors: topSupported(FLAVORS, flavorCounts, 5).map((item) => ({
      ...item,
      percent: percentOf(item.count, submittedCount),
    })),
    ring: buildFeelRing(feelCounts, submittedCount),
    participantPreferences: submitted.map((item) => ({
      feelId: item.feelId,
      styleIds: [...item.styleIds],
      flavorIds: [...item.flavorIds],
    })),
  };
}

export function buildVibeSnapshotPayload({
  hostUid,
  now = Date.now(),
  participants,
  presenceByUid,
  preferencesByUid,
}) {
  const crowd = aggregateCrowd({ participants, presenceByUid, preferencesByUid });
  return {
    schemaVersion: 1,
    createdAt: now,
    createdByHostUid: hostUid,
    activeParticipantCount: crowd.eligibleCount,
    submittedParticipantCount: crowd.submittedCount,
    feelCounts: crowd.feelCounts,
    styleCounts: crowd.styleCounts,
    flavorCounts: crowd.flavorCounts,
    participantPreferences: crowd.participantPreferences,
  };
}

export function votedLabel(submittedCount, eligibleCount) {
  return `Voted ${submittedCount}/${eligibleCount}`;
}
